function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import { combineTransactionSteps, getChangedRanges, findChildrenInRange, getMarksBetween, getAttributes, Mark, mergeAttributes, markPasteRule } from '@tiptap/core';
import { tokenize, find as _find, registerCustomProtocol, reset } from 'linkifyjs';
import { Plugin, PluginKey } from '@tiptap/pm/state';
/**
 * Check if the provided tokens form a valid link structure, which can either be a single link token
 * or a link token surrounded by parentheses or square brackets.
 *
 * This ensures that only complete and valid text is hyperlinked, preventing cases where a valid
 * top-level domain (TLD) is immediately followed by an invalid character, like a number. For
 * example, with the `find` method from Linkify, entering `example.com1` would result in
 * `example.com` being linked and the trailing `1` left as plain text. By using the `tokenize`
 * method, we can perform more comprehensive validation on the input text.
 */

function isValidLinkStructure(tokens) {
  if (tokens.length === 1) {
    return tokens[0].isLink;
  }

  if (tokens.length === 3 && tokens[1].isLink) {
    return ['()', '[]'].includes(tokens[0].value + tokens[2].value);
  }

  return false;
}
/**
 * This plugin allows you to automatically add links to your editor.
 * @param options The plugin options
 * @returns The plugin instance
 */


function autolink(options) {
  return new Plugin({
    key: new PluginKey('autolink'),
    appendTransaction: function appendTransaction(transactions, oldState, newState) {
      /**
       * Does the transaction change the document?
       */
      var docChanges = transactions.some(function (transaction) {
        return transaction.docChanged;
      }) && !oldState.doc.eq(newState.doc);
      /**
       * Prevent autolink if the transaction is not a document change or if the transaction has the meta `preventAutolink`.
       */

      var preventAutolink = transactions.some(function (transaction) {
        return transaction.getMeta('preventAutolink');
      });
      /**
       * Prevent autolink if the transaction is not a document change
       * or if the transaction has the meta `preventAutolink`.
       */

      if (!docChanges || preventAutolink) {
        return;
      }

      var tr = newState.tr;
      var transform = combineTransactionSteps(oldState.doc, _toConsumableArray(transactions));
      var changes = getChangedRanges(transform);
      changes.forEach(function (_ref) {
        var newRange = _ref.newRange;
        // Now let’s see if we can add new links.
        var nodesInChangedRanges = findChildrenInRange(newState.doc, newRange, function (node) {
          return node.isTextblock;
        });
        var textBlock;
        var textBeforeWhitespace;

        if (nodesInChangedRanges.length > 1) {
          // Grab the first node within the changed ranges (ex. the first of two paragraphs when hitting enter).
          textBlock = nodesInChangedRanges[0];
          textBeforeWhitespace = newState.doc.textBetween(textBlock.pos, textBlock.pos + textBlock.node.nodeSize, undefined, ' ');
        } else if (nodesInChangedRanges.length // We want to make sure to include the block seperator argument to treat hard breaks like spaces.
        && newState.doc.textBetween(newRange.from, newRange.to, ' ', ' ').endsWith(' ')) {
          textBlock = nodesInChangedRanges[0];
          textBeforeWhitespace = newState.doc.textBetween(textBlock.pos, newRange.to, undefined, ' ');
        }

        if (textBlock && textBeforeWhitespace) {
          var wordsBeforeWhitespace = textBeforeWhitespace.split(' ').filter(function (s) {
            return s !== '';
          });

          if (wordsBeforeWhitespace.length <= 0) {
            return false;
          }

          var lastWordBeforeSpace = wordsBeforeWhitespace[wordsBeforeWhitespace.length - 1];
          var lastWordAndBlockOffset = textBlock.pos + textBeforeWhitespace.lastIndexOf(lastWordBeforeSpace);

          if (!lastWordBeforeSpace) {
            return false;
          }

          var linksBeforeSpace = tokenize(lastWordBeforeSpace).map(function (t) {
            return t.toObject(options.defaultProtocol);
          });

          if (!isValidLinkStructure(linksBeforeSpace)) {
            return false;
          }

          linksBeforeSpace.filter(function (link) {
            return link.isLink;
          }) // Calculate link position.
          .map(function (link) {
            return _objectSpread(_objectSpread({}, link), {}, {
              from: lastWordAndBlockOffset + link.start + 1,
              to: lastWordAndBlockOffset + link.end + 1
            });
          }) // ignore link inside code mark
          .filter(function (link) {
            if (!newState.schema.marks.code) {
              return true;
            }

            return !newState.doc.rangeHasMark(link.from, link.to, newState.schema.marks.code);
          }) // validate link
          .filter(function (link) {
            return options.validate(link.value);
          }) // check whether should autolink
          .filter(function (link) {
            return options.shouldAutoLink(link.value);
          }) // Add link mark.
          .forEach(function (link) {
            if (getMarksBetween(link.from, link.to, newState.doc).some(function (item) {
              return item.mark.type === options.type;
            })) {
              return;
            }

            tr.addMark(link.from, link.to, options.type.create({
              href: link.href
            }));
          });
        }
      });

      if (!tr.steps.length) {
        return;
      }

      return tr;
    }
  });
}

function clickHandler(options) {
  return new Plugin({
    key: new PluginKey('handleClickLink'),
    props: {
      handleClick: function handleClick(view, pos, event) {
        var _a, _b;

        if (event.button !== 0) {
          return false;
        }

        if (!view.editable) {
          return false;
        }

        var a = event.target;
        var els = [];

        while (a.nodeName !== 'DIV') {
          els.push(a);
          a = a.parentNode;
        }

        if (!els.find(function (value) {
          return value.nodeName === 'A';
        })) {
          return false;
        }

        var attrs = getAttributes(view.state, options.type.name);
        var link = event.target;
        var href = (_a = link === null || link === void 0 ? void 0 : link.href) !== null && _a !== void 0 ? _a : attrs.href;
        var target = (_b = link === null || link === void 0 ? void 0 : link.target) !== null && _b !== void 0 ? _b : attrs.target;

        if (link && href) {
          window.open(href, target);
          return true;
        }

        return false;
      }
    }
  });
}

function pasteHandler(options) {
  return new Plugin({
    key: new PluginKey('handlePasteLink'),
    props: {
      handlePaste: function handlePaste(view, event, slice) {
        var state = view.state;
        var selection = state.selection;
        var empty = selection.empty;

        if (empty) {
          return false;
        }

        var textContent = '';
        slice.content.forEach(function (node) {
          textContent += node.textContent;
        });

        var link = _find(textContent, {
          defaultProtocol: options.defaultProtocol
        }).find(function (item) {
          return item.isLink && item.value === textContent;
        });

        if (!textContent || !link) {
          return false;
        }

        return options.editor.commands.setMark(options.type, {
          href: link.href
        });
      }
    }
  });
}

var pasteRegex = /https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,}\b(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)/gi; // From DOMPurify
// https://github.com/cure53/DOMPurify/blob/main/src/regexp.js
// eslint-disable-next-line no-control-regex

var ATTR_WHITESPACE = /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g;

function _isAllowedUri(uri, protocols) {
  var allowedProtocols = ['http', 'https', 'ftp', 'ftps', 'mailto', 'tel', 'callto', 'sms', 'cid', 'xmpp'];

  if (protocols) {
    protocols.forEach(function (protocol) {
      var nextProtocol = typeof protocol === 'string' ? protocol : protocol.scheme;

      if (nextProtocol) {
        allowedProtocols.push(nextProtocol);
      }
    });
  }

  return !uri || uri.replace(ATTR_WHITESPACE, '').match(new RegExp( // eslint-disable-next-line no-useless-escape
  "^(?:(?:".concat(allowedProtocols.join('|'), "):|[^a-z]|[a-z0-9+.-]+(?:[^a-z+.-:]|$))"), 'i'));
}
/**
 * This extension allows you to create links.
 * @see https://www.tiptap.dev/api/marks/link
 */


var Link = Mark.create({
  name: 'link',
  priority: 1000,
  keepOnSplit: false,
  exitable: true,
  onCreate: function onCreate() {
    if (this.options.validate && !this.options.shouldAutoLink) {
      // Copy the validate function to the shouldAutoLink option
      this.options.shouldAutoLink = this.options.validate;
      console.warn('The `validate` option is deprecated. Rename to the `shouldAutoLink` option instead.');
    }

    this.options.protocols.forEach(function (protocol) {
      if (typeof protocol === 'string') {
        registerCustomProtocol(protocol);
        return;
      }

      registerCustomProtocol(protocol.scheme, protocol.optionalSlashes);
    });
  },
  onDestroy: function onDestroy() {
    reset();
  },
  inclusive: function inclusive() {
    return this.options.autolink;
  },
  addOptions: function addOptions() {
    return {
      openOnClick: true,
      linkOnPaste: true,
      autolink: true,
      protocols: [],
      defaultProtocol: 'http',
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer nofollow',
        class: null
      },
      isAllowedUri: function isAllowedUri(url, ctx) {
        return !!_isAllowedUri(url, ctx.protocols);
      },
      validate: function validate(url) {
        return !!url;
      },
      shouldAutoLink: function shouldAutoLink(url) {
        return !!url;
      }
    };
  },
  addAttributes: function addAttributes() {
    return {
      href: {
        default: null,
        parseHTML: function parseHTML(element) {
          return element.getAttribute('href');
        }
      },
      target: {
        default: this.options.HTMLAttributes.target
      },
      rel: {
        default: this.options.HTMLAttributes.rel
      },
      class: {
        default: this.options.HTMLAttributes.class
      }
    };
  },
  parseHTML: function parseHTML() {
    var _this = this;

    return [{
      tag: 'a[href]',
      getAttrs: function getAttrs(dom) {
        var href = dom.getAttribute('href'); // prevent XSS attacks

        if (!href || !_this.options.isAllowedUri(href, {
          defaultValidate: function defaultValidate(url) {
            return !!_isAllowedUri(url, _this.options.protocols);
          },
          protocols: _this.options.protocols,
          defaultProtocol: _this.options.defaultProtocol
        })) {
          return false;
        }

        return null;
      }
    }];
  },
  renderHTML: function renderHTML(_ref2) {
    var _this2 = this;

    var HTMLAttributes = _ref2.HTMLAttributes;

    // prevent XSS attacks
    if (!this.options.isAllowedUri(HTMLAttributes.href, {
      defaultValidate: function defaultValidate(href) {
        return !!_isAllowedUri(href, _this2.options.protocols);
      },
      protocols: this.options.protocols,
      defaultProtocol: this.options.defaultProtocol
    })) {
      // strip out the href
      return ['a', mergeAttributes(this.options.HTMLAttributes, _objectSpread(_objectSpread({}, HTMLAttributes), {}, {
        href: ''
      })), 0];
    }

    return ['a', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },
  addCommands: function addCommands() {
    var _this3 = this;

    return {
      setLink: function setLink(attributes) {
        return function (_ref3) {
          var chain = _ref3.chain;
          var href = attributes.href;

          if (!_this3.options.isAllowedUri(href, {
            defaultValidate: function defaultValidate(url) {
              return !!_isAllowedUri(url, _this3.options.protocols);
            },
            protocols: _this3.options.protocols,
            defaultProtocol: _this3.options.defaultProtocol
          })) {
            return false;
          }

          return chain().setMark(_this3.name, attributes).setMeta('preventAutolink', true).run();
        };
      },
      toggleLink: function toggleLink(attributes) {
        return function (_ref4) {
          var chain = _ref4.chain;
          var href = attributes.href;

          if (!_this3.options.isAllowedUri(href, {
            defaultValidate: function defaultValidate(url) {
              return !!_isAllowedUri(url, _this3.options.protocols);
            },
            protocols: _this3.options.protocols,
            defaultProtocol: _this3.options.defaultProtocol
          })) {
            return false;
          }

          return chain().toggleMark(_this3.name, attributes, {
            extendEmptyMarkRange: true
          }).setMeta('preventAutolink', true).run();
        };
      },
      unsetLink: function unsetLink() {
        return function (_ref5) {
          var chain = _ref5.chain;
          return chain().unsetMark(_this3.name, {
            extendEmptyMarkRange: true
          }).setMeta('preventAutolink', true).run();
        };
      }
    };
  },
  addPasteRules: function addPasteRules() {
    var _this4 = this;

    return [markPasteRule({
      find: function find(text) {
        var foundLinks = [];

        if (text) {
          var _this4$options = _this4.options,
              protocols = _this4$options.protocols,
              defaultProtocol = _this4$options.defaultProtocol;

          var links = _find(text).filter(function (item) {
            return item.isLink && _this4.options.isAllowedUri(item.value, {
              defaultValidate: function defaultValidate(href) {
                return !!_isAllowedUri(href, protocols);
              },
              protocols: protocols,
              defaultProtocol: defaultProtocol
            });
          });

          if (links.length) {
            links.forEach(function (link) {
              return foundLinks.push({
                text: link.value,
                data: {
                  href: link.href
                },
                index: link.start
              });
            });
          }
        }

        return foundLinks;
      },
      type: this.type,
      getAttributes: function getAttributes(match) {
        var _a;

        return {
          href: (_a = match.data) === null || _a === void 0 ? void 0 : _a.href
        };
      }
    })];
  },
  addProseMirrorPlugins: function addProseMirrorPlugins() {
    var _this5 = this;

    var plugins = [];
    var _this$options = this.options,
        protocols = _this$options.protocols,
        defaultProtocol = _this$options.defaultProtocol;

    if (this.options.autolink) {
      plugins.push(autolink({
        type: this.type,
        defaultProtocol: this.options.defaultProtocol,
        validate: function validate(url) {
          return _this5.options.isAllowedUri(url, {
            defaultValidate: function defaultValidate(href) {
              return !!_isAllowedUri(href, protocols);
            },
            protocols: protocols,
            defaultProtocol: defaultProtocol
          });
        },
        shouldAutoLink: this.options.shouldAutoLink
      }));
    }

    if (this.options.openOnClick === true) {
      plugins.push(clickHandler({
        type: this.type
      }));
    }

    if (this.options.linkOnPaste) {
      plugins.push(pasteHandler({
        editor: this.editor,
        defaultProtocol: this.options.defaultProtocol,
        type: this.type
      }));
    }

    return plugins;
  }
});
export { Link, Link as default, _isAllowedUri as isAllowedUri, pasteRegex };